// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { query, where, getDocs, onSnapshot, updateDoc, doc, Timestamp } from "firebase/firestore";
import { useClient } from "../contexts/ClientContext";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  Switch,
  useMediaQuery,
  useTheme,
  Chip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popover,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import DomainIcon from "@mui/icons-material/Domain";
import StarOneLogo from "./StarOneLogo";
import SettingsNavIcon from "@mui/icons-material/ManageAccountsOutlined";
import { useThemeMode } from "../ThemeContext";
import { useSessionTimeout } from "../hooks/useSessionTimeout";
import dayjs from "dayjs";

const DRAWER_WIDTH = 270;

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
  roles: string[];
}

const navItems: NavItem[] = [
  { label: "Platform", path: "/platform", icon: <DomainIcon />, roles: ["super_admin"] },
  { label: "Dashboard", path: "/admin", icon: <AdminPanelSettingsIcon />, roles: ["admin"] },
  { label: "Timesheet", path: "/timesheet", icon: <AccessTimeIcon />, roles: ["receptionist", "admin"] },
  { label: "Leaves", path: "/leave", icon: <BeachAccessIcon />, roles: ["receptionist", "manager", "admin"] },
  { label: "Payroll", path: "/payroll", icon: <AttachMoneyIcon />, roles: ["manager", "admin"] },
  { label: "Reports", path: "/reports", icon: <AssessmentIcon />, roles: ["manager", "admin"] },
  { label: "Settings", path: "/settings", icon: <SettingsNavIcon />, roles: ["manager", "admin"] },
];

export default function AppLayout({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleTheme } = useThemeMode();
  const { showWarning, remainingSec, dismissWarning, doLogout } = useSessionTimeout();
  const { role, clientId, clientName, isPlatformAdmin, col } = useClient();

  // Notifications state
  interface Notification {
    id: string;
    message: string;
    read: boolean;
    createdAt: Timestamp;
    createdBy: string;
  }
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Listen for UID-targeted notifications (client-scoped; skip for platform admin)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user || isPlatformAdmin || !clientId) return;
    const unsub = onSnapshot(
      query(col("notifications"), where("targetUid", "==", user.uid)),
      (snap) => {
        const userNotifs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Notification, "id">) }));
        setNotifications((prev) => {
          const roleNotifs = prev.filter((n) => !(snap.docs.some((d) => d.id === n.id)));
          return [...userNotifs, ...roleNotifs].sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
        });
      },
      () => {}
    );
    return () => unsub();
  }, [clientId, isPlatformAdmin, col]);

  // Listen for role-based notifications (client-scoped; skip for platform admin)
  useEffect(() => {
    if (!role || isPlatformAdmin || !clientId) return;
    const unsub = onSnapshot(
      query(col("notifications"), where("targetRole", "==", role)),
      (snap) => {
        const roleNotifs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Notification, "id">) }));
        setNotifications((prev) => {
          const uidNotifs = prev.filter((n) => !roleNotifs.some((r) => r.id === n.id));
          return [...uidNotifs, ...roleNotifs].sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
        });
      },
      () => {}
    );
    return () => unsub();
  }, [role, clientId, isPlatformAdmin, col]);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const markAsRead = async (nId: string) => {
    try {
      // notifications live at clients/{clientId}/notifications/{id}
      await updateDoc(doc(db, "clients", clientId, "notifications", nId), { read: true });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    // Display name: prefer linked employee name, fall back to email
    setDisplayName(user.email ?? "");
    if (isPlatformAdmin || !clientId) return;
    getDocs(query(col("employees"), where("linkedAuthUid", "==", user.uid)))
      .then((snap) => {
        if (!snap.empty) {
          const data = snap.docs[0].data();
          const name = [data.firstName, data.lastName].filter(Boolean).join(" ");
          if (name) setDisplayName(name);
        }
      })
      .catch(() => {});
  }, [clientId, isPlatformAdmin, col]);

  const handleSignOut = async () => {
    setAnchorEl(null);
    await signOut(auth);
    navigate("/login");
  };

  const visibleNav = navItems.filter((item) => item.roles.includes(role));
  const roleLabel = isPlatformAdmin
    ? "Super Admin"
    : role === "admin"
    ? "Admin"
    : role === "receptionist"
    ? "Front Desk"
    : role.replace("_", " ");
  // Title: show client name for client users, generic for platform admin
  const headerTitle = isPlatformAdmin ? "Timesheet Platform" : clientName || "StarOne Timesheet";

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "auto" }}>
      <Toolbar />

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1.5, pt: 1 }}>
        {visibleNav.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => { navigate(item.path); setMobileOpen(false); }}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              py: 1.2,
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "#fff",
                "& .MuiListItemIcon-root": { color: "#fff" },
                "&:hover": { bgcolor: "primary.dark" },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }} />
          </ListItemButton>
        ))}
      </List>

      <Divider />

      {/* Theme Toggle */}
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        {mode === "light"
          ? <LightModeIcon fontSize="small" color="action" />
          : <DarkModeIcon fontSize="small" color="action" />}
        <Typography variant="body2" sx={{ flex: 1 }}>Dark Mode</Typography>
        <Switch size="small" checked={mode === "dark"} onChange={toggleTheme} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <StarOneLogo size={32} />
          <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 700, flexGrow: 1, fontSize: { xs: "1rem", sm: "1.15rem" } }}>
            {headerTitle}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            color={isPlatformAdmin ? "warning" : role === "admin" ? "primary" : role === "manager" ? "secondary" : "default"}
            sx={{ mr: 1.5, textTransform: "capitalize", fontWeight: 600, display: { xs: "none", sm: "flex" } }}
          />
          <IconButton onClick={(e) => setNotifAnchor(e.currentTarget)} size="small" sx={{ mr: 0.5 }}>
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { width: 360, maxHeight: 400, overflow: "auto" } }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
            </Box>
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                <NotificationsIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No notifications</Typography>
              </Box>
            ) : (
              notifications.map((n) => (
                <Box
                  key={n.id}
                  onClick={() => { if (!n.read) markAsRead(n.id); }}
                  sx={{
                    p: 2,
                    cursor: n.read ? "default" : "pointer",
                    bgcolor: n.read ? "transparent" : "action.hover",
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 700 }}>{n.message}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.createdAt ? dayjs(n.createdAt.toDate()).format("DD/MMM/YYYY hh:mm A") : ""}
                  </Typography>
                </Box>
              ))
            )}
          </Popover>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main", fontWeight: 700, fontSize: "0.9rem" }}>
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem disabled>
              <PersonIcon fontSize="small" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                <Typography variant="caption" color="text.secondary">{roleLabel}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignOut} sx={{ color: "error.main" }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", borderRight: 1, borderColor: "divider" },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar />
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, maxWidth: 1440, mx: "auto", width: "100%", boxSizing: "border-box" }}>
          {children}
        </Box>
      </Box>

      {/* Session Timeout Warning */}
      <Dialog open={showWarning} onClose={dismissWarning}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
          <TimerOffIcon color="warning" /> Session Expiring
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session will expire in <strong>{remainingSec}</strong> seconds due to inactivity.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click "Stay Logged In" to continue your session.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={doLogout} color="error">Sign Out</Button>
          <Button variant="contained" onClick={dismissWarning}>Stay Logged In</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
